import { MedicalResponse } from '@/modules/surgicalEvent/baseMedical.service';
import {
  EpicClinicalNotePayload,
  EpicDocument,
  EpicEncounter,
  EpicPatient,
  EpicPatientSyncPayload,
  EpicSurgicalEvent
} from './epic.model';
import {
  ID,
  ListApiResponse,
  SingleApiResponse,
  unwrapApiErrorDetails
} from '@ianesthesie-kit/services';
import axios, { AxiosResponse } from 'axios';
import { SurgicalEvent } from '@/modules/surgicalEvent';
import { stringify } from 'query-string';
import { removeEmptyValues } from '@ianesthesie-kit/core';
import { SearchByIdOrPersonalInfoParams } from '@/modules';

export class EpicService {
  public static searchEpicPatients(
    params: SearchByIdOrPersonalInfoParams
  ): Promise<MedicalResponse<EpicPatient, true>> {
    const qs = stringify(removeEmptyValues(params));
    return axios
      .get(`/bff/doctor/epic/patient?${qs}`)
      .then((response: AxiosResponse<ListApiResponse<EpicPatient>>) => ({
        data: response.data.data
      }))
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public static findEpicSurgicalEvent(
    patientId: ID
  ): Promise<MedicalResponse<EpicSurgicalEvent | null, false>> {
    return axios
      .get(this.getUrl(patientId, '?safe=true'))
      .then(
        (
          response: AxiosResponse<SingleApiResponse<EpicSurgicalEvent | null>>
        ) => ({
          data: response.data.data ?? null
        })
      )
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public static findEpicEncounters(
    patientId: ID
  ): Promise<MedicalResponse<EpicEncounter, true>> {
    return axios
      .get(this.getUrl(patientId, '/encounter'))
      .then((response: AxiosResponse<ListApiResponse<EpicEncounter>>) => ({
        data: response.data.data ?? null
      }))
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public static findEpicDocuments(
    patientId: ID
  ): Promise<MedicalResponse<EpicDocument, true>> {
    return axios
      .get(this.getUrl(patientId, '/document'))
      .then((response: AxiosResponse<ListApiResponse<EpicDocument>>) => ({
        data: response.data.data
      }))
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public static fetchEpicDocumentBase64Data(
    patientId: ID,
    documentId: ID
  ): Promise<MedicalResponse<string, false>> {
    return axios
      .get(this.getUrl(patientId, `/document/${documentId}/data`))
      .then((response: AxiosResponse<SingleApiResponse<string>>) => ({
        data: response.data.data
      }))
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public static manualLink(
    data: EpicPatientSyncPayload
  ): Promise<MedicalResponse<EpicSurgicalEvent, false>> {
    return axios
      .post(this.getUrl(data?.patientId, `/manual/${data?.extPatientId}`))
      .then(
        (response: AxiosResponse<SingleApiResponse<EpicSurgicalEvent>>) => ({
          data: response.data.data
        })
      )
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public static syncData(
    patientId: ID
  ): Promise<MedicalResponse<SurgicalEvent, false>> {
    return axios
      .post(this.getUrl(patientId, '/sync-data'))
      .then((response: AxiosResponse<SingleApiResponse<SurgicalEvent>>) => ({
        data: response.data.data
      }))
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  public static createClinicalNote(
    patientId: ID,
    payload: EpicClinicalNotePayload
  ): Promise<MedicalResponse<void, false>> {
    return axios
      .post(this.getUrl(patientId, '/clinical-note'), payload)
      .then(() => ({
        data: {} as any
      }))
      .catch((err) => {
        throw unwrapApiErrorDetails(err) || err;
      });
  }

  private static getUrl(patientId: ID, seg?: string): string {
    return `/bff/doctor/patient/${patientId}/surgical-event/active/epic-se${seg}`;
  }
}
